import React from "react";
import suitesLogoAsset from "@/assets/icons/Lerch_Suites_Logo_Creme.png.asset.json";
const suitesLogo = suitesLogoAsset.url;
import scrollDown from "@/assets/icons/Scroll_down.svg";
import { useLocale } from "@/contexts/LocaleContext";
import { EditableT } from "@/cms/EditableT";
import { EditableVideo } from "@/cms/Editable";

interface HeroProps {
  videoSrc?: string;
  /** Kleinere Videodatei für Mobilgeräte */
  videoSrcMobile?: string;
  posterSrc?: string;
  /** Optional responsive srcset (WebP) for the poster image */
  posterSrcSet?: string;
  posterSizes?: string;
  /** CMS id for the editable hero video, e.g. "home.hero.video" */
  videoId?: string;
}

const Hero: React.FC<HeroProps> = ({ videoSrc, videoSrcMobile, posterSrc, posterSrcSet, posterSizes, videoId }) => {
  const { t } = useLocale();
  const subline = t("home.hero.subline");
  const useVideo = !!videoSrc;

  return (
    <section
      aria-label={t("home.hero.aria_label")}
      className="relative w-full h-[100svh] min-h-[560px] overflow-hidden bg-neutral-800 text-[hsl(0_0%_95%)] -mt-[72px] lg:-mt-[88px] 2xl:-mt-[103px]"
    >
      {useVideo ? (
        <EditableVideo
          id={videoId ?? "hero.video"}
          src={videoSrc!}
          srcMobile={videoSrcMobile}
          poster={posterSrc}
          className="absolute inset-0 h-full w-full object-cover [object-position:50%_10%]"
          wrapperClassName="absolute inset-0 block"
        />
      ) : posterSrc ? (
        <img
          src={posterSrc}
          srcSet={posterSrcSet}
          sizes={posterSizes ?? "100vw"}
          alt=""
          width={1920}
          height={1080}
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover [object-position:50%_10%]"
          aria-hidden="true"
        />
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsl(0_0%_30%)_0%,_hsl(0_0%_15%)_70%)]"
        />
      )}


      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent via-30% to-black/85"
      />

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center animate-[heroIn_1200ms_ease-out_both]">
        <div aria-hidden className="invisible flex flex-col items-center pointer-events-none">
          <span
            className="block font-['Kumbh_Sans'] uppercase font-light text-[22px] sm:text-[26px] lg:text-[28px] xl:text-[32px] leading-tight tracking-[0.05em] max-w-[18ch] whitespace-pre-line mt-[120px] lg:mt-[186px]"
          >
            {subline}
          </span>
          <span className="mt-[38px] inline-flex">
            <img src={scrollDown} alt="" width={32} height={40} className="h-10 w-8" loading="lazy" decoding="async" />
          </span>
        </div>

        <img
          src={suitesLogo}
          alt="Lerch Suites"
          width={524}
          height={207}
          decoding="async"
          loading="eager"
          className="w-[min(70vw,18rem)] sm:w-[min(55vw,22rem)] md:w-[min(45vw,28rem)] lg:w-[min(40vw,420px)] xl:w-[524px] h-auto"
        />
        <span
          className="block font-['Kumbh_Sans'] uppercase font-light text-[hsl(0_0%_95%)] text-[22px] sm:text-[26px] lg:text-[28px] xl:text-[32px] leading-tight tracking-[0.05em] max-w-[18ch] whitespace-pre-line mt-[120px] lg:mt-[186px]"
        >
          <EditableT k="home.hero.subline" multiline />
        </span>
        <a
          href="#apartments-in-kappl"
          aria-label="Nach unten scrollen"
          className="mt-[38px] inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(0_0%_95%)] rounded-full"
        >
          <img
            src={scrollDown}
            alt=""
            width={32}
            height={40}
            decoding="async"
            className="h-10 w-8 animate-bounce [animation-duration:2.4s]"
            aria-hidden="true" loading="lazy"
          />
        </a>
      </div>
    </section>
  );
};

export default Hero;
